/**
 * Property ↔ model assignments (Model Config under Property → Config).
 *
 * Angular: property-model-config + selected_models on POST /api/property/:id.
 * Rows: property.current_models; catalog: GET /api/model/active/list.
 */
import apiClient from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import { CANNOT_DELETE_MODEL_MAPPING_MESSAGE, DEFAULT_DELETE_CONFIRM_MESSAGE } from '../constants'
import type {
  PropertyModelCatalogItem,
  PropertyModelRow,
  SelectedModelPayloadItem,
} from '../types'
import { unwrapActiveListPayload } from '../utils/propertyConfigEditLogic'
import { parseCanEditProductMappingResponse } from './propertyProductService'

export { CANNOT_DELETE_MODEL_MAPPING_MESSAGE }

export function extractPropertyModels(
  payload: Record<string, unknown> | null
): PropertyModelRow[] {
  if (!payload) return []
  const raw = payload.current_models
  if (!Array.isArray(raw)) return []
  const seen = new Set<number | string>()
  const unique: PropertyModelRow[] = []
  for (const item of raw as PropertyModelRow[]) {
    const id = item.property_model_map_id ?? item.id
    if (id != null && seen.has(id as number | string)) continue
    if (id != null) seen.add(id as number | string)
    unique.push({
      ...item,
      property_model_map_id: item.property_model_map_id ?? item.id,
    })
  }
  return unique
}

/** Angular: GET /api/model/active/list */
export async function fetchActiveModelsList(): Promise<PropertyModelCatalogItem[]> {
  const { data } = await apiClient.get<{ data?: unknown } | unknown[]>(API_ENDPOINTS.MODEL_ACTIVE_LIST, {
    params: { perPage: 100 },
  })
  return unwrapActiveListPayload(data) as PropertyModelCatalogItem[]
}

/**
 * Build Angular `selected_models` JSON for multipart property POST.
 * Skips Base (model_id === 1) and empty model ids.
 */
export function buildSelectedModelsJson(rows: SelectedModelPayloadItem[]): string | null {
  const saved: SelectedModelPayloadItem[] = []
  const unsaved: SelectedModelPayloadItem[] = []
  for (const row of rows) {
    const modelId = Number(row.model_id)
    if (!modelId || modelId === 1) continue
    const formatted: SelectedModelPayloadItem = {
      model_id: modelId,
      effective_from: row.effective_from,
      effective_to: row.effective_to,
    }
    if (row.property_model_map_id != null && Number.isFinite(row.property_model_map_id)) {
      formatted.property_model_map_id = Number(row.property_model_map_id)
      saved.push(formatted)
    } else {
      unsaved.push(formatted)
    }
  }
  const selected = saved.concat(unsaved.reverse())
  return selected.length > 0 ? JSON.stringify(selected) : null
}

export async function deletePropertyModelRow(
  propertyModelMapId: string | number
): Promise<void> {
  const n = Number(String(propertyModelMapId).trim())
  if (!Number.isFinite(n)) {
    throw new Error('Invalid property_model_map_id for delete')
  }
  await apiClient.delete(`/api/property/delete_model_mapping/${n}`)
}

function extractApiErrorMessage(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: unknown }; message?: string }
  const body = ax.response?.data
  if (typeof body === 'string' && body.trim()) return body.trim()
  if (body && typeof body === 'object') {
    const o = body as Record<string, unknown>
    const msg = o.message ?? o.msg ?? o.error ?? o.Message
    if (msg != null && String(msg).trim()) return String(msg).trim()
  }
  if (ax.message && String(ax.message).trim()) return String(ax.message).trim()
  return fallback
}

export async function fetchCanDeleteModelMapping(
  propertyModelMapId: string | number
): Promise<{ allowed: boolean; message?: string }> {
  const n = Number(String(propertyModelMapId).trim())
  if (!Number.isFinite(n)) {
    return { allowed: false, message: 'Invalid model mapping id.' }
  }
  try {
    const { data } = await apiClient.get<unknown>(`/api/property/can_delete_model_mapping/${n}`)
    let message = DEFAULT_DELETE_CONFIRM_MESSAGE
    if (typeof data === 'string' && data.trim()) {
      message = data.trim()
    } else if (data && typeof data === 'object') {
      const o = data as Record<string, unknown>
      const msg = o.message ?? o.msg ?? o.Message
      if (msg != null && String(msg).trim()) message = String(msg).trim()
    }
    return { allowed: true, message }
  } catch (e: unknown) {
    return {
      allowed: false,
      message: extractApiErrorMessage(e, CANNOT_DELETE_MODEL_MAPPING_MESSAGE),
    }
  }
}

/**
 * Angular: GET /api/property/can_edit_model_mapping/:propertyModelMapId
 * Reuses product can_edit response parsing (same payload shapes).
 */
export async function fetchCanEditModelMapping(
  propertyModelMapId: string | number
): Promise<{ allowed: boolean; message?: string }> {
  const n = Number(String(propertyModelMapId).trim())
  if (!Number.isFinite(n)) {
    return { allowed: false, message: 'Invalid model mapping id.' }
  }
  try {
    const { data } = await apiClient.get<unknown>(`/api/property/can_edit_model_mapping/${n}`)
    return parseCanEditProductMappingResponse(data)
  } catch (e: unknown) {
    const ax = e as { response?: { data?: unknown } }
    const body = ax.response?.data
    if (body !== undefined) {
      return parseCanEditProductMappingResponse(body)
    }
    return { allowed: false, message: 'Could not verify whether this model mapping can be edited.' }
  }
}
